// ============================================================
// COMBAT ACTIONS - the catalog's vocabulary.
//
// An action is what the drum offers: a cost the log ADVISES (nothing is
// ever blocked - the table adjudicates), requirements the UI disables
// on, a SECTION that groups it in the cylinder, and an EXECUTION that
// says what pressing it does.
//
// `execution` and `category` used to be one field, and it did two jobs
// badly (v8.300.0). `category` is presentation - attacks wear the attack
// colour. `execution` is dispatch, and the drum's handler is an
// exhaustive switch over it with no per-id branching, so a new action is
// a catalog row and at most one resolver.
// ============================================================
import type { QuietKind } from '../attackBeat';
import type { AttackToggleState } from './toggleEntries';
import type { TestSubject } from '../opposedTests';
import type { QuietMove } from '../stealth/proposals';

/** Advisory only - displayed and logged, never enforced. */
export type ActionCost = 'free' | 'half' | 'full' | 'halfOrFull' | 'reaction';

/**
 * Presentation only since v8.300.0 - `execution` decides behaviour. Its
 * one real job is the attack colour, which marks the plate that is the
 * point of the whole cylinder.
 */
type ActionCategory = 'attack' | 'stance' | 'opposed' | 'utility';

/**
 * What pressing the plate does.
 *
 * - `chamber` - loads the trigger; nothing happens until it fires.
 * - `engage` - writes a state (a toggle, a condition) and stops there.
 * - `engageAndFire` - writes that state AND pulls the trigger, because
 *   the action can only be followed by an attack.
 * - `instant` - resolves on the spot, with no weapon involved.
 * - `ownStance` - switches one of the acting character's own stances
 *   (`stanceId`): a plate the character brings, not a catalog row.
 * - `killTeam` - one of the kill-team's verbs (`killTeam`): Squad Mode's
 *   plates, appended for a brother (engine/actions/killTeamPlates).
 */
type ActionExecution = 'chamber' | 'engage' | 'engageAndFire' | 'instant' | 'ownStance' | 'killTeam';

/** Which run of the cylinder an action belongs to. */
export type ActionSection = 'attack' | 'stance' | 'melee' | 'ranged' | 'movement' | 'quiet' | 'leave' | 'own' | 'squad';

export const SECTION_LABELS: Record<ActionSection, string> = {
  attack: 'ATTACK',
  stance: 'STANCES',
  melee: 'MELEE',
  ranged: 'RANGED',
  movement: 'MOVEMENT',
  quiet: 'QUIET WORK',
  leave: 'LEAVE',
  // The acting character's own stances (engine/actions/stancePlates); the catalog's are postures.
  own: 'YOUR SOLO MODES',
  // A brother's Squad Mode plates (engine/actions/killTeamPlates), after his own stances.
  squad: 'SQUAD MODE',
};

/** What an `engage` action writes. Both may be present. */
interface ActionEngagement {
  /** An attack toggle, set through `applyStanceToggle` so exclusions hold. */
  toggleKey?: keyof AttackToggleState;
  /** A condition code written onto the ACTOR. */
  conditionCode?: string;
  /**
   * A mode the NEXT attack is fired in (v8.307.0). Not a toggle: it is
   * true for exactly one shot and cleared when the dice leave the cup,
   * and AttackToggleState must gain no keys - its 128-mask parity sweep
   * has to keep meaning 2^7.
   */
  attackMode?: string;
}

/**
 * A purely NARRATIVE opposed test (v8.305.0): both sides roll, the log
 * names a winner and says what it means, and the app changes nothing
 * else. Manoeuvre and Grapple are deliberately this - the GM moves the
 * tokens and adjudicates the hold, because a grapple STATE would be a
 * subsystem (links between combatants, escape actions, a submenu) for a
 * thing two people at a table settle in one sentence.
 *
 * `{winner}` and `{loser}` are substituted in the verdict lines.
 */
interface OpposedNarration {
  /**
   * What each side rolls (v8.439.0). Combat's rows name the same
   * characteristic twice; a stealth row pits one skill against another
   * (Concealment against Awareness).
   */
  actor: TestSubject;
  defender: TestSubject;
  /** Grapple: +1 Degree of Success per step of size difference. */
  sizeBonus?: boolean;
  onActorWin: string;
  onDefenderWin: string;
  onStandoff: string;
}

/**
 * QUIET WORK (v8.441.0) - a Stealth plate that is a test: what the one
 * acting rolls, and what the sharpest of the opposition answers with
 * (`null` = nobody answers, a test on its own). `move` is the row of the
 * proposal table it reads (`engine/stealth/proposals`). The test is not
 * rolled where the plate is pressed: it is put to the GM, whose screen
 * rolls it and proposes what it does to the ladder.
 */
interface QuietWork {
  move: QuietMove;
  actor: TestSubject;
  defender: TestSubject | null;
}

/**
 * What the TRIGGER does when it fires a chambered plate. Absent = the
 * opposed action it names (Feint, Knock-Down...). `attack` fires the armed
 * weapon as it is (a Takedown IS an attack: the scene supplies its edge).
 * `quiet` puts the plate's quiet work to the GM.
 */
type ChamberFires = 'attack' | 'quiet';

export interface ActionDef {
  id: string;
  name: string;
  cost: ActionCost;
  category: ActionCategory;
  execution: ActionExecution;
  section: ActionSection;
  engages?: ActionEngagement;
  /** Set on a narrative opposed action - see OpposedNarration. */
  opposes?: OpposedNarration;
  /** Set on a Stealth plate that is a test - see QuietWork. */
  quiet?: QuietWork;
  /** What the trigger does with this plate chambered - see ChamberFires. */
  fires?: ChamberFires;
  /** An `ownStance` plate: the stance of the acting character it switches. */
  stanceId?: string;
  /** A `killTeam` plate: the verb it presses, and the ability (or the running one) it is about. */
  /** A Squad Mode plate's verb; a start plate carries the ability's Cohesion cost, for the strip to show. */
  killTeam?: {
    verb: 'enter' | 'challenge' | 'leave' | 'start' | 'join';
    abilityId?: string;
    key?: string;
    cost?: number;
  };
  /** The current target must not have noticed the party (engine/unaware, Stealth's ladder). */
  requiresUnawareTarget?: boolean;
  /** The UI disables the plate (with this as the reason) when unmet. */
  requiresMelee?: boolean;
  requiresRanged?: boolean;
  requiresFullAuto?: boolean;
  requiresProne?: boolean;
  /** Standing: Go Prone is no use to someone already down (2026-10-26). */
  requiresUpright?: boolean;
  /** Engaged in melee with an enemy, by the map (engine/battlefield/disengage): Disengage (2026-10-26). */
  requiresEngaged?: boolean;
  /** Needs a direction to fire along - the measuring tape or a target. */
  requiresBearing?: boolean;
  /**
   * A strike: with a melee weapon, the target must be within its reach on the
   * map (strict, footprint to footprint - plan map-display, D). Defensive
   * Stance and Gang Up strike nobody, so do not say it.
   */
  requiresReach?: boolean;
  /**
   * A charge: the run-up on the map, not the reach (engine/battlefield/charge, 2026-10-21) - at least 4 m, within
   * the charge distance, on a line that can be run. The run brings the striker into reach.
   */
  requiresChargeLane?: boolean;
  /** A vehicle's alone: the drum leaves it off any other actor's cylinder (Ram - plan map-display, D). */
  vehicleOnly?: boolean;
  /** Never a vehicle's: a vehicle's charge is its Ram (plan map-display, the vehicle rulings). */
  personOnly?: boolean;
  /**
   * Pressable while the actor is Pinned (2026-10-26): the book leaves a pinned figure snap fire, a crawl or going
   * prone - every other plate dims with DRUM_REASONS.pinned.
   */
  whilePinned?: true;
  /** Body to body with the target on the map, 1 m: a ram, and Knock-Down (the body, not a weapon). */
  requiresContact?: boolean;
  /** The target off the actor's bow, when the map can say (a ram runs straight along the heading). */
  requiresBow?: boolean;
  requiresActor?: boolean;
  requiresTarget?: boolean;
  /** One line for the plate. */
  blurb: string;
  /**
   * A body action's beat on the map (plan design-gaps K2; the Track K review): what it plays as - the body ram or
   * the feint - and what the target's marker reads when it won. A row without one writes no beat (Manoeuvre).
   */
  beat?: ActionBeat;
}

/** How a body action plays on the map, and the word its target's marker wears when it won. */
interface ActionBeat {
  plays: 'ram' | 'feint' | QuietKind;
  verb: string;
}

export const COST_LABELS: Record<ActionCost, string> = {
  free: 'Free Action',
  half: 'Half Action',
  full: 'Full Action',
  halfOrFull: 'Half or Full Action',
  reaction: 'Reaction',
};

/**
 * The Standard Attack IS the empty chamber: the trigger holds `null`
 * and fires the armed weapon. Naming the sentinel here keeps the
 * component from branching on an id.
 */
export const DEFAULT_ACTION_ID = 'standardAttack';

/** The chamber id to store for a `chamber` action - null for the default. */
export const chamberFor = (def: ActionDef): string | null => (def.id === DEFAULT_ACTION_ID ? null : def.id);

/**
 * Section captions, keyed by the id of the FIRST def of each run. The
 * catalog array is already presentation order, so the runs come from it
 * directly - a second ordered list would be a second thing to keep in
 * step, and it would drift.
 */
export function sectionCaptions(defs: ActionDef[]): Record<string, string> {
  const out: Record<string, string> = {};
  let prev: ActionSection | null = null;
  for (const def of defs) {
    if (def.section !== prev) out[def.id] = SECTION_LABELS[def.section];
    prev = def.section;
  }
  return out;
}
